import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import ToggleButtons from "../../../../base/button/toggle_buttons";
import {ReactComponent as SlideshowIcon} from "../../../../../../assets/images/user-platform/slide-show.svg";
import {ReactComponent as CategoryIcon} from "../../../../../../assets/images/user-platform/category.svg";
import {ReactComponent as LargeProductIcon} from "../../../../../../assets/images/user-platform/large-product.svg";
import {ReactComponent as MediumProductIcon} from "../../../../../../assets/images/user-platform/medium-product.svg";
import {ReactComponent as SmallProductIcon} from "../../../../../../assets/images/user-platform/small-product.svg";
import {ReactComponent as TimeBasedIcon} from "../../../../../../assets/images/user-platform/time-based.svg";
import {ReactComponent as BannerIcon} from "../../../../../../assets/images/user-platform/banner.svg";
import {ReactComponent as ImageCarouselIcon} from "../../../../../../assets/images/user-platform/image-carousel.svg";
import {UserHomepageControllerContext, UserHomepageStateContext} from "../../../../../containers/user-homepage";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {Col, Collapse} from "reactstrap";
import CreateNewUserHomepageEntity from "../../../../../containers/user-homepage/ui/dialogs/create-new-entity";
import {UserHomepageComponentTypes, UserHomepageViewMode} from "../../../../../containers/user-homepage/models";
import Button from "../../../../base/button/button";
import * as Yup from "yup";
import ValidateMessages from "../../../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import {createUUId} from "../../../../../../core/services/utils/utils";
import classnames from "classnames";
import {OutlinedInputForm} from "../../../../base/input/outlined_input";
import IconButton from "../../../../base/button/icon_button";
import {Edit} from "@mui/icons-material";
import SaveOrRollback from "../../../save-or-rollback";
import Form from "../../../../base/form";
import {Fade} from "@mui/material";
import ObserversService from "../../../../../../core/services/observers";
import Api from "../../../../../../core/services/api/api";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import useCanManageHomepage from "../../../../../containers/user-homepage/hooks/use-can-manage-homepage";
import debounce from "lodash/debounce";
import EnvService from "../../../../../../core/services/env";


const UserPlatformComponents = {
    slideShow: 1,
    category: 2,
    largeProduct: 3,
    mediumProduct: 4,
    smallProduct: 5,
    timeBased: 6,
    banner: 7,
    imageCarousel: 8,
}

const userPlatformComponentIcons = {
    [UserPlatformComponents.slideShow]: <SlideshowIcon/>,
    [UserPlatformComponents.category]: <CategoryIcon/>,
    [UserPlatformComponents.largeProduct]: <LargeProductIcon/>,
    [UserPlatformComponents.mediumProduct]: <MediumProductIcon/>,
    [UserPlatformComponents.smallProduct]: <SmallProductIcon/>,
    [UserPlatformComponents.timeBased]: <TimeBasedIcon/>,
    [UserPlatformComponents.banner]: <BannerIcon/>,
    [UserPlatformComponents.imageCarousel]: <ImageCarouselIcon/>,
}


const initCreateComponentDialog = {open: false, typeId: undefined};

const UserPlatformSingleEditionHomepageTopbar = () => {
    const controller = useContext(UserHomepageControllerContext);
    const state = useContext(UserHomepageStateContext);
    const [createComponentDialog, setCreateComponentDialog] = useState(initCreateComponentDialog);
    const [creating, setCreating] = useState(false);
    const [settingAsLive, setSettingAsLive] = useState(false);
    const buttonsContainerId = useRef(createUUId(true));
    const containerId = useRef(createUUId(true));
    const isMounted = useIsMounted();
    const selectedTabId = useMemo(() => state.selection?.tabId, [state?.selection?.tabId])
    /** @type {boolean}*/
    const showToggleButtons = useMemo(() =>
            state.viewMode === UserHomepageViewMode.edit && !!selectedTabId,
        [state.viewMode, selectedTabId])
    const operatorAccessLevels = useAccessLevels();
    const canManageHomepage = useCanManageHomepage();
    const hasHomepageManageFlag =
        operatorAccessLevels.packages.clientPlatform.manageWebHomepageEditions ||
        operatorAccessLevels.packages.clientPlatform.managePhoneHomepageEditions;

    const userPlatformComponentTypes = Object.values(UserPlatformComponents).map((value) => ({
        value: value,
        name: (function () {
            switch (value) {
                case UserPlatformComponents.slideShow:
                    return "Slide Show";
                case UserPlatformComponents.category:
                    return "Category";
                case UserPlatformComponents.largeProduct:
                    return "Large Product";
                case UserPlatformComponents.mediumProduct:
                    return "Medium Product";
                case UserPlatformComponents.smallProduct:
                    return "Small Product";
                case UserPlatformComponents.timeBased:
                    return "Time Based";
                case UserPlatformComponents.banner:
                    return "Banner";
                case UserPlatformComponents.imageCarousel:
                    return "Image Carousel";
                default:
                    return "";
            }
        })(),
        icon: userPlatformComponentIcons[value.toString()]
    }));

    /**
     * Creates a new component for the currently selected tab.
     *
     * @param formValues
     * @param componentTypeId
     * @return {Promise<boolean | undefined>}
     */
    const createComponent = async (formValues, componentTypeId) => {
        if (!canManageHomepage || !hasHomepageManageFlag) return;
        const forApi = {
            tabId: selectedTabId,
            typeId: componentTypeId,
            title: formValues.title,
            industryId: formValues.industry?.id,
        }
        setCreating(true);
        const response = await Api.createHomepageEditionTabComponent(forApi);
        if (!isMounted())
            return;
        setCreating(false);
        if (response?.resultFlag) {
            controller.createComponent(selectedTabId, response.data)
        }
        return response?.resultFlag;
    }

    /**
     * Sets this edition as the live edition of the current partner.
     */
    const setAsLiveEdition = async () => {
        if (!canManageHomepage || !hasHomepageManageFlag) return;
        setSettingAsLive(true);
        const response = await Api.setHomepageEditionAsLive(state.id);
        if (!isMounted())
            return;
        setCreating(false);
        if (response?.resultFlag) {
            controller.setAsLive();
        }
    }

    /**
     * Changes the view mode of the current homepage edition.
     */
    const changeViewMode = () => {
        switch (state.viewMode) {
            case UserHomepageViewMode.edit:
                return controller.changeViewMode(UserHomepageViewMode.preview);
            case UserHomepageViewMode.preview:
            default:
                return controller.changeViewMode(UserHomepageViewMode.edit);
        }
    }

    /**
     * Gets user confirmation for setting this edition as the live version of the partner's homepage.
     */
    const getLiveEditionConfirmation = () => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Do you want to make this Edition public? it will become available to everyone.
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (confirmed) => confirmed && setAsLiveEdition(),
            "Proceed",
            "cancel"
        );
    }

    const toggleButtonOptions = useMemo(() => {
        const res = userPlatformComponentTypes ?? [];
        if (!operatorAccessLevels.packages.clientPlatform.bannerManagement) {
            res.splice(res.findIndex(e => e.value === UserPlatformComponents.banner), 1);
            res.splice(res.findIndex(e => e.value === UserPlatformComponents.slideShow), 1);
        }
        // TODO: REMOVE THIS.
        if (!EnvService.MockHomeImageCarousel) {
            res.splice(res.findIndex(e => e.value === UserPlatformComponents.imageCarousel), 1);
        }
        return userPlatformComponentTypes?.map(e => ({
            value: e.value,
            name: (
                <div className={'name'}>
                    {e.icon}
                    <p>
                        {e.name}
                    </p>
                </div>
            ),
            disabled: creating,
        }))
    }, [creating, operatorAccessLevels.packages.clientPlatform.bannerManagement, userPlatformComponentTypes])

    const toggleButtons = useMemo(() => (
        <Collapse className={'w-100'} isOpen={showToggleButtons}>
            <div>
                <ToggleButtons
                    className={'topbar-toggle-buttons'}
                    value={undefined}
                    onChange={(_, value) => setCreateComponentDialog({open: true, typeId: value})}
                    options={toggleButtonOptions}
                />
            </div>
        </Collapse>
    ), [toggleButtonOptions, showToggleButtons])

    return (
        <>
            <div className={'d-flex flex-wrap'}>
                <Fade in={!!state.updatedData?.tabs}>
                    <div id={containerId.current} className={'homepage-topbar-header'}>
                        <TitleSection
                            headerId={containerId.current}
                            buttonsContainerId={buttonsContainerId.current}
                        />
                        <div id={buttonsContainerId.current} className={'homepage-topbar-header-button'}>
                            {
                                state?.updatedData?.isLive
                                    ? <>
                                        <div className={'live-version'}>
                                            Live Edition
                                        </div>
                                    </>
                                    : <Button
                                        disabled={settingAsLive}
                                        onClick={getLiveEditionConfirmation}
                                        variant={'outlined'}
                                        color={'primary'}
                                        size={'medium'}
                                    >
                                        Set Edition as live
                                    </Button>
                            }
                            {
                                state?.updatedData?.canEdit &&
                                <Button
                                    disabled={creating}
                                    onClick={changeViewMode}
                                    variant={'outlined'}
                                    color={'primary'}
                                    size={'medium'}
                                >
                                    {
                                        state.viewMode === UserHomepageViewMode.preview
                                            ? "Edit the Edition"
                                            : "Preview the Edition"
                                    }
                                </Button>
                            }
                        </div>
                    </div>
                </Fade>
                {toggleButtons}
            </div>
            <CreateNewUserHomepageEntity
                label={"Component"}
                open={createComponentDialog.open}
                close={() => setCreateComponentDialog(initCreateComponentDialog)}
                create={(formValues) => createComponent(formValues, createComponentDialog.typeId)}
                showIndustry={createComponentDialog.typeId === UserHomepageComponentTypes.category}
            />
        </>
    )
}

const schema = Yup.object().shape({
    title: Yup.string().trim().nullable().required(ValidateMessages.required)
});

const validate = makeValidate(schema);
const required = makeRequired(schema);

const minTitleWidth = 150;

const TitleSection = ({headerId, buttonsContainerId}) => {
    const controller = useContext(UserHomepageControllerContext);
    const state = useContext(UserHomepageStateContext);
    const [inEditMode, setInEditMode] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [width, setWidth] = useState(minTitleWidth);
    const titleId = useRef(createUUId(true));
    const containerId = useRef(createUUId(true));
    const isMounted = useIsMounted();
    /**@type {React.MutableRefObject<formApi>}*/
    const formRef = useRef();

    const operatorAccessLevels = useAccessLevels();
    const canManageHomepage = useCanManageHomepage();
    const hasHomepageManageFlag =
        operatorAccessLevels.packages.clientPlatform.manageWebHomepageEditions ||
        operatorAccessLevels.packages.clientPlatform.managePhoneHomepageEditions;

    const initialValues = useMemo(() => ({
        title: state.currentData?.name,
    }), [state.currentData?.name])

    const canEdit = useMemo(() =>
            state.viewMode === UserHomepageViewMode.edit && state.updatedData?.canEdit,
        [state.viewMode, state.updatedData?.canEdit])

    /**
     * With each change in the headerId:
     * - attaches a resize observer that would adjust the maximum width of the title input.
     */
    useEffect(() => {
        const ele = document.getElementById(headerId);
        if (!ele)
            return;
        const observer = ObserversService.newResizeObserver(debounce(adjustMaxWidth));
        ObserversService.observeResizeObserver(observer, ele)
        adjustMaxWidth([ele]);
        return () => {
            ObserversService.disconnectResizeObserver(observer);
        }
    }, [headerId])

    /**
     * With each change in the value of canEdit memo:
     * - if we can not edit the edition and we are in edit mode, reverts the title changes.
     */
    useEffect(() => {
        if (!canEdit && inEditMode) {
            revertTitleChanges(formRef.current);
        }
    }, [canEdit])


    /**
     * With each change in the title of the state:
     * - updates the width of the title input so that the edit icon can be positioned properly
     */
    useEffect(() => {
        updateWidth([document.getElementById(titleId.current)]);
    }, [initialValues.title])

    /**
     * As soon as the component mounts:
     * - attaches an observer to observe the change in the title input content.
     */
    useEffect(() => {
        const title = document.getElementById(titleId.current);
        if (!title)
            return;
        const observer = ObserversService.newResizeObserver(debounce(updateWidth));
        ObserversService.observeResizeObserver(observer, title);
        return () => {
            ObserversService.disconnectResizeObserver(observer)
        }
    }, [])

    /**
     * Adjusts the maximum width that this component can occupy.
     * @param {ResizeObserverEntry[] | Element[]} entries
     */
    const adjustMaxWidth = (entries) => {
        if (!entries?.length)
            return;

        const containerWidth = entries[0]?.contentRect?.width ?? entries[0].getBoundingClientRect()?.width ?? 0;
        const buttonsWidth = document.getElementById(buttonsContainerId)?.getBoundingClientRect()?.width ?? 0;
        const form = document.getElementById(containerId.current);

        try {
            form.style.maxWidth = `${containerWidth - buttonsWidth - 170}px`;
        } catch (e) {
        }
    }

    /**
     * Updates the width of the title input so that the edit icon can be positioned properly
     * @param {ResizeObserverEntry[] | Element[]} entries
     */
    const updateWidth = (entries) => {
        if (!entries?.length)
            return;
        const entry = entries[0];
        const entryWidth = entry?.contentRect?.width ?? entry?.getBoundingClientRect()?.width ?? 0;
        const width = Math.max(entryWidth + 20, minTitleWidth);
        setWidth(width);
    }

    /**
     * Updates the title of the current component in the server.
     *
     * @param values
     * @return {Promise<void>}
     */
    const submitForm = async (values) => {
        if (!canManageHomepage || !hasHomepageManageFlag) return;
        if (values.title === initialValues.title) {
            return setInEditMode(false);
        }
        const forApi = {
            id: state.id,
            name: values.title,
        }
        setUpdating(true);
        const response = await Api.updateHomepageEdition(forApi);
        if (!isMounted())
            return;
        setUpdating(false);
        if (response?.resultFlag) {
            updateTitle(values.title, true);
        }
    }

    /**
     * Changes the state's title without committing it.
     * @param {InputEvent} e
     * @param {formApi} form
     */
    const liveChangeTitle = (e, form) => {
        const newTitle = e.target.value ?? '';
        form.change(e.target.name, newTitle);
        updateTitle(newTitle, false);
    }

    /**
     * Reverts the title changes back to its initial values.
     * @param {formApi} form
     */
    const revertTitleChanges = (form) => {
        form.change('title', initialValues.title);
        form.resetFieldState('title');
        updateTitle(initialValues.title, true);
    }

    /**
     * Updates the title of the state in the controller.
     *
     * * if committed, then exits the edit mode.
     * @param {string} title
     * @param {boolean} commit
     */
    const updateTitle = (title, commit = false) => {
        controller.updateTitle(title ?? "", commit)
        if (commit) {
            setInEditMode(false);
        }
    }

    return (
        <>
            <Form
                id={containerId.current}
                onSubmit={submitForm}
                validate={validate}
                initialValues={initialValues}
                className={classnames('user-homepage-input-container', {inEditMode})}
                render={({values, form}) => {
                    if (!formRef.current)
                        formRef.current = form;

                    return (
                        <>
                            <Col className={'flex-fill'}>
                                <p id={titleId.current} className={'invisible'}>
                                    {values.title ?? ''}
                                </p>
                                <OutlinedInputForm
                                    fullWidth
                                    style={{
                                        '--width': `${width}px`
                                    }}
                                    disabled={!inEditMode}
                                    placeholder={inEditMode ? 'title...' : ''}
                                    name={'title'}
                                    className={'mr-2'}
                                    required={required.title}
                                    value={values.title ?? ''}
                                    onChange={(e) => liveChangeTitle(e, form)}
                                    InputProps={{
                                        endAdornment: (
                                            !inEditMode && canEdit &&
                                            <IconButton
                                                disabled={updating}
                                                color={'paper.secondary'}
                                                onPressed={() => setInEditMode(true)}
                                            >
                                                <Edit className={"icon normal"}/>
                                            </IconButton>
                                        ),
                                    }}
                                />
                            </Col>
                            <Col xs={'auto'} className={'d-flex align-items-center p-0'}>
                                <SaveOrRollback
                                    show={inEditMode}
                                    loading={updating}
                                    column={false}
                                    onRollback={() => revertTitleChanges(form)}
                                    onSave={form.submit}
                                />
                            </Col>
                        </>
                    )
                }}
            />
        </>
    );
}


export default UserPlatformSingleEditionHomepageTopbar;
